import type { Ocupante } from './ocupantes'
import type { TipoColisao } from './timelineCinematica'

export interface MetricasProjetilHumano {
  massaKg: number
  velocidadeKmh: number
  energiaCineticaJoules: number
  forcaImpactoNewton: number
  forcaImpactoKgf: number
  toneladasEquivalentes: number
  efeitoNoCondutor: string
  licaoDoutrinaria: string
}

export interface AvaliacaoChoqueCraniano {
  ocorre: boolean
  descricao: string
  riscoLesao: string
  tecnologiaAtenuacao: string
}

export interface AvaliacaoEjecaoParcial {
  ocorre: boolean
  mecanismo: string
  riscoTraumatico: string
  condutaPrevencao: string
}

/**
 * Calcula a física do ocupante traseiro sem cinto como um "projétil humano".
 */
export function calcularForcaProjetilHumano(
  massaKg = 75,
  velocidadeKmh = 50,
  distanciaParadaM = 0.15,
): MetricasProjetilHumano {
  const velocidadeMs = velocidadeKmh / 3.6
  const energiaCineticaJoules = Math.round(0.5 * massaKg * Math.pow(velocidadeMs, 2))
  // Pelo teorema do trabalho e energia: W = F * d => F = Ec / d
  const forcaImpactoNewton = Math.round(energiaCineticaJoules / distanciaParadaM)
  const forcaImpactoKgf = Math.round(forcaImpactoNewton / 9.81)
  const toneladasEquivalentes = Number((forcaImpactoKgf / 1000).toFixed(1))

  return {
    massaKg,
    velocidadeKmh,
    energiaCineticaJoules,
    forcaImpactoNewton,
    forcaImpactoKgf,
    toneladasEquivalentes,
    efeitoNoCondutor: `O ocupante traseiro colide contra as costas do banco dianteiro com força equivalente a ~${forcaImpactoKgf} kgf (${toneladasEquivalentes} t). O encosto do banco colapsa para a frente, esmagando o motorista (mesmo que este esteja usando cinto) contra o volante e o painel.`,
    licaoDoutrinaria: 'O cinto de segurança no banco traseiro não serve apenas para proteger o passageiro traseiro: ele é a única barreira que impede que o banco da frente seja esmagado.',
  }
}

/**
 * Avalia a probabilidade de impacto crânio-com-crânio entre ocupantes adjacentes na colisão lateral.
 */
export function avaliarChoqueCranioCraniano(
  tipoColisao: TipoColisao,
  ocupantes: readonly Ocupante[],
  temAirbagCentral = false,
): AvaliacaoChoqueCraniano {
  const ehLateral = tipoColisao === 'lateral' || tipoColisao === 'lateralComCapotamento'
  const pessoasDianteiras = ocupantes.filter(
    (o) =>
      o.tipo === 'pessoa' &&
      (o.idAncora === 'motorista' || o.idAncora === 'passageiro' || o.idAncora === 'passageiroDianteiro'),
  )

  if (!ehLateral || pessoasDianteiras.length < 2) {
    return {
      ocorre: false,
      descricao: 'Sem risco de choque crânio-craniano adjacente na configuração atual.',
      riscoLesao: 'Nenhum contato inter-ocupante identificado.',
      tecnologiaAtenuacao: 'N/A',
    }
  }

  if (temAirbagCentral) {
    return {
      ocorre: false,
      descricao: 'Airbag Central (Far-side Airbag) acionado entre os assentos dianteiros.',
      riscoLesao: 'Contato mútuo prevenido pela bolsa de ar intermediária.',
      tecnologiaAtenuacao: 'Bolsa inflável central impede a flexão extrema do tronco e choque crânio-facial direto.',
    }
  }

  return {
    ocorre: true,
    descricao: 'Ambos os ocupantes sofrem aceleração lateral acentuada; a defasagem no retorno elástico do cinto projeta os crânios em choque frontal direto no plano central.',
    riscoLesao: 'TCE severo mútuo com fraturas de calota craniana e trauma de face sem qualquer barreira atenuadora.',
    tecnologiaAtenuacao: 'Veículos modernos com tecnologia EuroNCAP utilizam Airbags Centrais (Far-side Airbags) entre os encostos dos bancos dianteiros para mitigar esse choque.',
  }
}

/**
 * Avalia o risco de ejeção parcial de membros superiores durante o capotamento ou tombamento.
 */
export function avaliarEjecaoParcialMembros(
  tipoColisao: TipoColisao,
  rolagemGraus: number,
): AvaliacaoEjecaoParcial {
  const temRolagem = tipoColisao === 'capotamento' || tipoColisao === 'tombamento' || tipoColisao === 'lateralComCapotamento'

  if (!temRolagem || Math.abs(rolagemGraus) < 30) {
    return {
      ocorre: false,
      mecanismo: 'Envelope do habitáculo preserva a contenção dos membros.',
      riscoTraumatico: 'Sem projeção centrífuga identificada.',
      condutaPrevencao: 'Manter ocupante contido no assento.',
    }
  }

  return {
    ocorre: true,
    mecanismo: 'A força centrífuga e a ausência de tônus muscular (ou reflexo instintivo de apoio nas janelas) projetam os membros superiores para fora das janelas com vidros estilhaçados.',
    riscoTraumatico: 'Esmagamento catastrófico do membro contra o pavimento à medida que o veículo gira e apoia o peso da carroceria no solo. Alto risco de amputação traumática e desluvamento cutâneo extenso.',
    condutaPrevencao: 'No atendimento APH, inspeção imediata das aberturas da cabine sob o veículo antes de qualquer movimentação da carcaça;',
  }
}
